import normalizeUrl from 'normalize-url'
import type { ReactNode } from 'react'
import React, { createContext, useMemo } from 'react'

import type { GetOverviewResponse } from '@fourthwall/services/dist/MembershipsCreatorApiClient'
import { useCurrentShopQuery } from '@modules/Dashboard/hooks/useCurrentShopQuery'
import { usePodcastsQuery } from '@modules/Dashboard/pages/Apps/pages/AppsSpotify/hooks/usePodcastsQuery'
import { useTwitchMembershipsDiscountQuery } from '@modules/Dashboard/pages/Apps/pages/AppsTwitchDiscounts'
import { useSocialLinksQuery } from '@modules/ThemeEditorV2/modules/SocialLinks'

import { useMembersQuery } from '../../Members/hooks/useMembersQuery'
import { useTiersQuery } from '../../Perks/hooks/useTiersQuery'
import { usePostsQuery } from '../../Posts/hooks/usePostsQuery'
import { useMembershipsOverviewQuery } from '../hooks/useMembershipsOverviewQuery'
import { useMembershipsStatusQuery } from '../hooks/useMembershipsStatusQuery'
import { useMembershipsOnboarding } from '../modules/Onboarding/hooks/useMembershipsOnboarding'

type Props = { children: ReactNode }

type OverviewContextType = {
  membershipsStatus: ('Live' | 'Disabled' | 'NonPublic' | 'Inactive') | null
  membershipsUrl: {
    label: string
    href: string
    uri: string
  } | null
  topComments: GetOverviewResponse['topComments']['comments'] | null
  newMembers: GetOverviewResponse['members']['newest'] | null
  onboarding: ReturnType<typeof useMembershipsOnboarding>
  hasDiscordPerk: boolean | null
  hasWelcomePost: boolean | null
  hasAnyPost: boolean | null
  hasAnyAudioPost: boolean | null
  hasAnyPodcast: boolean | null
  hasAnyTier: boolean | null
  hasTwitchSocialLink: boolean | null
  hasTwitchMembershipsDiscount: boolean | null
  membersCount: number | null
  isLoading: boolean
}

export const OverviewContext = createContext<OverviewContextType | undefined>(undefined)

export const OverviewProvider = ({ children }: Props) => {
  const { currentShopQuery } = useCurrentShopQuery()
  const { tiersQuery } = useTiersQuery()
  const { postsQuery } = usePostsQuery([{ limit: 1, state: 'Published' }])
  const audioPostQuery = usePostsQuery([{ limit: 1, state: 'Published', 'types[]': ['Audio'] }])
  const { podcastsQuery } = usePodcastsQuery()
  const { membershipsOverviewQuery } = useMembershipsOverviewQuery()
  const { membershipsStatusQuery } = useMembershipsStatusQuery()
  const { membersQuery } = useMembersQuery([{ page: 1, limit: 10 }])
  const { twitchMembershipsDiscountQuery } = useTwitchMembershipsDiscountQuery([], {
    onError: () => {}, // NOTE: Error is expected, it means that there is no discount app set up
  })
  const { socialLinksQuery } = useSocialLinksQuery([currentShopQuery.data?.id!], {
    enabled: currentShopQuery.isSuccess && !!currentShopQuery.data.id,
  })
  const onboarding = useMembershipsOnboarding({
    page: 'MembershipsOverview',
  })

  const membershipsUrl = useMemo(() => {
    if (!currentShopQuery.data) return null

    return {
      label: normalizeUrl(`${currentShopQuery.data.primaryDomain}/supporters`, {
        stripProtocol: true,
        stripWWW: true,
      }),
      href: `${currentShopQuery.data.baseUri}/supporters`,
      uri: currentShopQuery.data.baseUri,
    }
  }, [currentShopQuery.data])

  const value = useMemo(
    () => ({
      membershipsStatus: membershipsStatusQuery.data ? membershipsStatusQuery.data.status : null,
      membershipsUrl,
      topComments: membershipsOverviewQuery.data
        ? membershipsOverviewQuery.data.topComments.comments
        : null,
      newMembers: membershipsOverviewQuery.data
        ? membershipsOverviewQuery.data.members.newest
        : null,
      onboarding,
      hasDiscordPerk: membershipsOverviewQuery.data
        ? membershipsOverviewQuery.data.perks.discord.enabled
        : null,
      hasWelcomePost: membershipsOverviewQuery.data
        ? !!membershipsOverviewQuery.data.welcomePost
        : null,
      hasAnyPost: postsQuery.data ? postsQuery.data.posts.length > 0 : null,
      hasAnyAudioPost: audioPostQuery.postsQuery.data
        ? audioPostQuery.postsQuery.data.posts.length > 0
        : null,
      hasAnyPodcast: podcastsQuery.data ? podcastsQuery.data.podcasts.length > 0 : null,
      hasAnyTier: tiersQuery.data ? tiersQuery.data.tiers.length > 0 : null,
      hasTwitchSocialLink: socialLinksQuery.data
        ? socialLinksQuery.data.socialNetworks.some(({ name }) => name === 'twitch')
        : null,
      hasTwitchMembershipsDiscount: !!twitchMembershipsDiscountQuery.data,
      membersCount: membersQuery.data ? membersQuery.data.members.length : null,
      isLoading:
        currentShopQuery.isInitialLoading ||
        tiersQuery.isInitialLoading ||
        membersQuery.isInitialLoading ||
        membershipsOverviewQuery.isInitialLoading ||
        membershipsStatusQuery.isInitialLoading ||
        socialLinksQuery.isInitialLoading ||
        twitchMembershipsDiscountQuery.isInitialLoading ||
        postsQuery.isInitialLoading ||
        onboarding.isLoading,
    }),
    [
      membershipsUrl,
      tiersQuery,
      membershipsOverviewQuery,
      membershipsStatusQuery,
      socialLinksQuery,
      onboarding,
      twitchMembershipsDiscountQuery,
      postsQuery,
      audioPostQuery,
      podcastsQuery,
      membersQuery,
      currentShopQuery.isInitialLoading,
    ],
  )

  return <OverviewContext.Provider value={value}>{children}</OverviewContext.Provider>
}
